import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck, Calculator } from "lucide-react";
import PrivacyLink from "./PrivacyLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = ["PCN", "GP Practice", "ICB", "GP Federation", "Other"];

const inputCls =
  "w-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";

const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const EnquiryForm = ({ journey = "general", defaultRequirement = "", calcSummary = [], heading = "Send us an enquiry" }) => {
  const [form, setForm] = useState({
    full_name: "", organisation: "", org_type: "PCN", email: "",
    phone: "", requirement: defaultRequirement, start_date: "", additional: "", website: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (defaultRequirement) setForm((f) => ({ ...f, requirement: defaultRequirement }));
  }, [defaultRequirement]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        start_date: form.start_date || null,
        additional: form.additional || null,
        journey,
        calculator: calcSummary.length ? calcSummary : null,
      });
      setSent(true);
      toast.success("Enquiry submitted — our clinical services team will respond shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="enquiry-success"
        className="bg-white border border-[#10B981]/30 rounded-[2rem] p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
      >
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Enquiry received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your enquiry is with our clinical team now —
          a real person will read it and reply, usually within one working day. A confirmation
          email is on its way to {form.email}.
        </p>
        <button
          data-testid="enquiry-new-button"
          onClick={() => {
            setSent(false);
            setForm({ full_name: "", organisation: "", org_type: "PCN", email: "", phone: "", requirement: defaultRequirement, start_date: "", additional: "", website: "" });
          }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="enquiry-form"
      className="bg-white border border-[#E2E8F0] rounded-[2rem] p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
    >
      <h2 className="font-display text-2xl font-bold text-[#0B192C]">{heading}</h2>
      <p className="mt-2 text-sm text-[#64748B]">
        Two minutes to complete — no account needed. Fields marked * are required.
      </p>

      {calcSummary.length > 0 && (
        <div data-testid="enquiry-calc-summary" className="mt-6 bg-[#0B192C] rounded-2xl p-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#10B981]">
            <Calculator size={14} /> Your calculator estimate (indicative)
          </p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
            {calcSummary.map((c) => (
              <div key={c.label}>
                <p className="text-[11px] uppercase tracking-wider text-white/45">{c.label}</p>
                <p className="text-sm font-bold text-white mt-0.5">{c.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor={`enq-name-${journey}`} className={labelCls}>Full name *</label>
          <input id={`enq-name-${journey}`} data-testid="enquiry-input-name" required minLength={2}
            value={form.full_name} onChange={update("full_name")} placeholder="Dr. Jane Smith" className={inputCls} />
        </div>
        <div>
          <label htmlFor={`enq-org-${journey}`} className={labelCls}>Organisation name *</label>
          <input id={`enq-org-${journey}`} data-testid="enquiry-input-organisation" required minLength={2}
            value={form.organisation} onChange={update("organisation")} placeholder="Riverside PCN" className={inputCls} />
        </div>
        <div>
          <label htmlFor={`enq-orgtype-${journey}`} className={labelCls}>Organisation type *</label>
          <select id={`enq-orgtype-${journey}`} data-testid="enquiry-select-orgtype"
            value={form.org_type} onChange={update("org_type")} className={inputCls}>
            {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`enq-email-${journey}`} className={labelCls}>Work email *</label>
          <input id={`enq-email-${journey}`} data-testid="enquiry-input-email" type="email" required
            value={form.email} onChange={update("email")} placeholder="j.smith@nhs.net" className={inputCls} />
        </div>
        <div>
          <label htmlFor={`enq-phone-${journey}`} className={labelCls}>Telephone number *</label>
          <input id={`enq-phone-${journey}`} data-testid="enquiry-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={update("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor={`enq-start-${journey}`} className={labelCls}>Preferred start date</label>
          <input id={`enq-start-${journey}`} data-testid="enquiry-input-startdate" type="date"
            value={form.start_date} onChange={update("start_date")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`enq-req-${journey}`} className={labelCls}>Service or workforce required *</label>
          <input id={`enq-req-${journey}`} data-testid="enquiry-input-requirement" required minLength={2}
            value={form.requirement} onChange={update("requirement")}
            placeholder="e.g. Structured Medication Reviews + 2 clinical pharmacists" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`enq-add-${journey}`} className={labelCls}>Additional requirements</label>
          <textarea id={`enq-add-${journey}`} data-testid="enquiry-input-additional" rows={4}
            value={form.additional} onChange={update("additional")}
            placeholder="Practices covered, patient numbers, clinical responsibilities, timescales..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor={`enq-web-${journey}`}>Website</label>
          <input id={`enq-web-${journey}`} data-testid="enquiry-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={update("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Please do not include patient-identifiable or confidential clinical information. Your
          business contact details are processed under UK GDPR solely to respond to your enquiry.
          All estimates are indicative and subject to formal quotation, clinical governance,
          competency requirements, availability and contract. Read our <PrivacyLink testid="enquiry-privacy-link" />.
        </p>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="enquiry-submit-btn"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>Submitting <Loader2 size={16} className="animate-spin" /></>
        ) : (
          <>Submit enquiry <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>
        )}
      </button>
    </form>
  );
};

export default EnquiryForm;
